import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, DollarSign, 
  Save, Loader2, TrendingUp, 
  TrendingDown,
  AlertCircle, Package,
  Calculator, BarChart3, X
} from 'lucide-react';
import { incomingService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { Toast } from '../../../../components';
import './IncomingPricingPage.css';

interface PricingItem {
  id: number;
  product_id: number;
  product_name: string;
  sku: string;
  quantity: number;
  xml_unit: string;
  total_price: number;
  stock_quantity: number | null;
  cost_price: number;
  current_price: number;
  suggested_price: number;
  new_price: number;
  markup: number;
  margin: number;
}

interface InvoiceEntry {
  id: number;
  number: string;
  supplier_name?: string;
  launched_status?: string | null;
  launched_at?: string | null;
  status?: string;
}

const IncomingPricingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<PricingItem[]>([]);
  const [invoiceEntry, setInvoiceEntry] = useState<InvoiceEntry | null>(null);
  const [stockLaunched, setStockLaunched] = useState(false);

  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingItem, setConvertingItem] = useState<PricingItem | null>(null);
  const [conversionType, setConversionType] = useState<'multiply' | 'divide'>('multiply');
  const [conversionValue, setConversionValue] = useState('');
  const [isConverting, setIsConverting] = useState(false);

  const loadPricingData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await incomingService.getPricing(parseInt(id!));
      
      if (response.success && response.data) {
        setInvoiceEntry(response.data.invoiceEntry);
        setStockLaunched(Boolean(response.data.stock_launched));
        
        const formattedItems = response.data.items.map((item: any) => {
          const costPrice = parseFloat(item.cost_price) || 0;
          const currentPrice = parseFloat(item.current_price) || 0;
          const suggestedPrice = parseFloat(item.suggested_price) || 0;
          const newPrice = suggestedPrice > 0 ? suggestedPrice : currentPrice;
          
          let markup = 0;
          let margin = 0;
          if (newPrice > 0 && costPrice > 0) {
            markup = ((newPrice - costPrice) / costPrice) * 100;
            margin = ((newPrice - costPrice) / newPrice) * 100;
          }
          
          return {
            id: item.id,
            product_id: item.product_id,
            product_name: item.product_name || 'Produto sem nome',
            sku: item.sku || 'N/A',
            quantity: parseFloat(item.quantity) || 0,
            xml_unit: item.xml_unit || 'UN',
            total_price: parseFloat(item.total_price) || 0,
            stock_quantity:
              item.stock_quantity === null || item.stock_quantity === undefined
                ? null
                : parseFloat(item.stock_quantity),
            cost_price: costPrice,
            current_price: currentPrice,
            suggested_price: suggestedPrice,
            new_price: newPrice,
            markup,
            margin
          };
        });
        
        setItems(formattedItems);
      } else {
        showToast('Erro ao carregar dados de precificação', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao carregar dados de precificação:', error);
      showToast(error.response?.data?.message || 'Erro ao carregar dados de precificação', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    if (id) {
      loadPricingData();
    }
  }, [id, loadPricingData]);

  const handlePriceChange = (idx: number, val: number) => {
    const newItems = [...items];
    const item = { ...newItems[idx] };
    item.new_price = val;
    
    if (val > 0 && item.cost_price > 0) {
      item.markup = ((val - item.cost_price) / item.cost_price) * 100;
      item.margin = ((val - item.cost_price) / val) * 100;
    } else {
      item.markup = 0;
      item.margin = 0;
    }
    
    newItems[idx] = item;
    setItems(newItems);
  };

  const openConvertModal = (item: PricingItem) => {
    setConvertingItem(item);
    setConversionType('multiply');
    setConversionValue('');
    setShowConvertModal(true);
  };

  const previewConvertedQty = useMemo(() => {
    if (!convertingItem || !conversionValue) return null;
    const value = parseFloat(conversionValue);
    if (isNaN(value) || value <= 0) return null;
    const current = Number(convertingItem.quantity || 0);
    const result = conversionType === 'multiply' ? current * value : current / value;
    return Math.round(result);
  }, [convertingItem, conversionValue, conversionType]);

  const previewUnitCost = useMemo(() => {
    if (!convertingItem || previewConvertedQty === null || previewConvertedQty <= 0) return null;
    const total =
      convertingItem.total_price > 0
        ? convertingItem.total_price
        : convertingItem.quantity * convertingItem.cost_price;
    return total / previewConvertedQty;
  }, [convertingItem, previewConvertedQty]);

  const handleConvertQuantity = async () => {
    if (!convertingItem || !id || !conversionValue) {
      showToast('Preencha o valor de conversão', 'error');
      return;
    }

    const value = parseFloat(conversionValue);
    if (isNaN(value) || value <= 0) {
      showToast('O valor deve ser um número positivo', 'error');
      return;
    }

    setIsConverting(true);
    try {
      const data = conversionType === 'multiply'
        ? { multiplier: value }
        : { divisor: value };

      const response = await incomingService.convertItemQuantity(
        parseInt(id),
        convertingItem.id,
        data
      );

      if (response.success) {
        const before = response.data?.quantity_before ?? convertingItem.quantity;
        const after = response.data?.quantity_after ?? previewConvertedQty;
        const stockMsg = response.data?.stock_adjusted
          ? ' Estoque ajustado automaticamente.'
          : '';
        showToast(
          `Quantidade corrigida: ${before} → ${after}.${stockMsg}`,
          'success'
        );
        setShowConvertModal(false);
        setConvertingItem(null);
        setConversionValue('');
        await loadPricingData();
      } else {
        showToast(response.message || 'Erro ao converter quantidade', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao converter quantidade:', error);
      showToast(error.response?.data?.message || 'Erro ao converter quantidade', 'error');
    } finally {
      setIsConverting(false);
    }
  };

  const handleFinish = async () => {
    setIsProcessing(true);
    try {
      const pricingItems = items
        .filter(item => {
          const hasProductId = item.product_id && item.product_id > 0;
          const hasValidPrice = item.new_price && item.new_price > 0;
          if (!hasProductId || !hasValidPrice) {
            console.warn('⚠️ [IncomingPricingPage] Item inválido ignorado:', item);
          }
          return hasProductId && hasValidPrice;
        })
        .map(item => ({
          id: item.id,
          product_id: item.product_id,
          new_price: parseFloat(item.new_price.toString())
        }));

      if (pricingItems.length === 0) {
        showToast('Nenhum item válido para aplicar preços. Verifique se todos os itens têm produto vinculado e preço válido.', 'error');
        setIsProcessing(false);
        return;
      }

      const response = await incomingService.updatePricing(parseInt(id!), pricingItems);
      
      if (response.success) {
        showToast('Preços aplicados com sucesso!', 'success');
        navigate('/erp/stock/incoming');
      } else {
        showToast(response.message || 'Erro ao aplicar preços', 'error');
      }
    } catch (error: any) {
      console.error('❌ [IncomingPricingPage] Erro ao aplicar preços:', error);
      showToast(error.response?.data?.message || 'Erro ao aplicar preços', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const totalImpact = useMemo(() => {
    return items.reduce((acc, item) => acc + (item.new_price - item.current_price), 0);
  }, [items]);

  const averageMargin = useMemo(() => {
    if (items.length === 0) return 0;
    const totalMargin = items.reduce((acc, item) => acc + item.margin, 0);
    return totalMargin / items.length;
  }, [items]);

  const averageMarkup = useMemo(() => {
    if (items.length === 0) return 0;
    const totalMarkup = items.reduce((acc, item) => acc + item.markup, 0);
    return totalMarkup / items.length;
  }, [items]);

  const lowMarginItems = useMemo(() => {
    return items.filter(item => item.margin < 30 && item.margin > 0).length;
  }, [items]);

  const suspiciousQtyItems = useMemo(() => {
    return items.filter(item => {
      const unit = (item.xml_unit || '').toUpperCase();
      const looksLikeBox = ['CX', 'PC', 'PCT', 'DZ', 'FD'].includes(unit);
      return looksLikeBox && item.quantity <= 5;
    }).length;
  }, [items]);

  if (loading) {
    return (
      <div className="incoming-pricing-loading">
        <Loader2 size={32} className="incoming-pricing-spinner" />
        <p>Carregando dados de precificação...</p>
      </div>
    );
  }

  return (
    <div className="incoming-pricing">
      <Toast toast={toast} hideToast={hideToast} />
      
      <div className="incoming-pricing-header">
        <div className="incoming-pricing-header-left">
          <button 
            onClick={() => navigate('/erp/stock/incoming')} 
            className="incoming-pricing-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="incoming-pricing-title-row">
              <h1 className="incoming-pricing-title">Precificação {id}</h1>
              <span className="incoming-pricing-badge">Ajuste de Margens</span>
              {stockLaunched && (
                <span className="incoming-pricing-badge stock">Estoque lançado</span>
              )}
            </div>
            <p className="incoming-pricing-subtitle">
              Origem: {invoiceEntry?.supplier_name || 'N/A'} • NF-{invoiceEntry?.number || 'N/A'}
            </p>
          </div>
        </div>

        <div className="incoming-pricing-header-right">
          <button 
            onClick={handleFinish}
            disabled={isProcessing}
            className="incoming-pricing-finish-btn"
          >
            {isProcessing ? <Loader2 className="incoming-pricing-spinner" size={16} /> : <Save size={16} />}
            Aplicar Novos Preços
          </button>
        </div>
      </div>

      <div className="incoming-pricing-content">
        <div className="incoming-pricing-list">
          {items.map((item, idx) => {
            const unit = (item.xml_unit || '').toUpperCase();
            const looksSuspicious =
              ['CX', 'PC', 'PCT', 'DZ', 'FD'].includes(unit) && item.quantity <= 5;

            return (
              <div
                key={item.id}
                className={`incoming-pricing-item ${looksSuspicious ? 'qty-warning' : ''}`}
              >
                <div className="incoming-pricing-item-grid">
                  <div className="incoming-pricing-item-info">
                    <div className="incoming-pricing-item-icon">
                      <Package size={28} />
                    </div>
                    <div className="incoming-pricing-item-details">
                      <p className="incoming-pricing-item-sku">{item.sku}</p>
                      <h4 className="incoming-pricing-item-name">{item.product_name}</h4>
                      <div className="incoming-pricing-item-cost">
                        <span>Custo un.: R$ {item.cost_price.toFixed(2)}</span>
                        <span className="incoming-pricing-qty-chip">
                          Qtd NF: {item.quantity} {item.xml_unit || 'UN'}
                        </span>
                        {item.stock_quantity !== null && (
                          <span className="incoming-pricing-qty-chip muted">
                            Estoque: {item.stock_quantity}
                          </span>
                        )}
                      </div>
                      {looksSuspicious && (
                        <p className="incoming-pricing-qty-hint">
                          Unidade do XML sugere caixa/pacote com pouca quantidade — verifique conversão.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="incoming-pricing-item-controls">
                    <div className="incoming-pricing-control-group">
                      <label className="incoming-pricing-control-label">Preço Atual</label>
                      <div className="incoming-pricing-control-display">
                        R$ {item.current_price.toFixed(2)}
                      </div>
                      <button
                        type="button"
                        className="incoming-pricing-convert-btn"
                        onClick={() => openConvertModal(item)}
                        title="Corrigir quantidade (ex.: CX → UN)"
                      >
                        <Calculator size={14} />
                        Corrigir qtd
                      </button>
                    </div>

                    <div className="incoming-pricing-control-group">
                      <label className="incoming-pricing-control-label primary">Novo Preço (Venda)</label>
                      <div className="incoming-pricing-control-input-wrapper">
                        <DollarSign size={14} className="incoming-pricing-control-icon" />
                        <input 
                          type="number" 
                          step="0.01"
                          value={item.new_price}
                          onChange={(e) => handlePriceChange(idx, parseFloat(e.target.value) || 0)}
                          className="incoming-pricing-control-input"
                        />
                      </div>
                    </div>

                    <div className="incoming-pricing-control-group">
                      <label className="incoming-pricing-control-label">Margem / Markup</label>
                      <div className="incoming-pricing-metrics">
                        <div className="incoming-pricing-metric">
                          <span className="incoming-pricing-metric-label">Margem:</span>
                          <span className={`incoming-pricing-metric-value ${item.margin < 30 ? 'warning' : 'success'}`}>
                            {item.margin.toFixed(1)}%
                          </span>
                        </div>
                        <div className="incoming-pricing-metric">
                          <span className="incoming-pricing-metric-label">Markup:</span>
                          <span className="incoming-pricing-metric-value primary">
                            {item.markup.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="incoming-pricing-sidebar">
          <div className="incoming-pricing-summary">
            <div className="incoming-pricing-summary-glow" />
            
            <h3 className="incoming-pricing-summary-title">
              <Calculator size={16} className="incoming-pricing-summary-icon" /> Análise de Rentabilidade
            </h3>

            <div className="incoming-pricing-summary-content">
              <div className="incoming-pricing-summary-section">
                <p className="incoming-pricing-summary-label">Impacto no Faturamento</p>
                <div className="incoming-pricing-summary-impact">
                  <p className={`incoming-pricing-summary-impact-value ${totalImpact >= 0 ? 'positive' : 'negative'}`}>
                    {totalImpact >= 0 ? '+' : ''} R$ {totalImpact.toFixed(2)}
                  </p>
                  {totalImpact !== 0 && (
                    <div className={`incoming-pricing-summary-impact-icon ${totalImpact > 0 ? 'positive' : 'negative'}`}>
                      {totalImpact > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    </div>
                  )}
                </div>
              </div>

              <div className="incoming-pricing-summary-stats">
                <div className="incoming-pricing-summary-stat">
                  <p className="incoming-pricing-summary-stat-label">Margem Média</p>
                  <p className="incoming-pricing-summary-stat-value">{averageMargin.toFixed(1)}%</p>
                </div>
                <div className="incoming-pricing-summary-stat">
                  <p className="incoming-pricing-summary-stat-label">Markup Médio</p>
                  <p className="incoming-pricing-summary-stat-value">{averageMarkup.toFixed(1)}%</p>
                </div>
              </div>

              <div className="incoming-pricing-summary-suggestion">
                <BarChart3 size={18} className="incoming-pricing-summary-suggestion-icon" />
                <p className="incoming-pricing-summary-suggestion-text">
                  Se a margem/markup parecer absurda frente ao último preço, confira se a quantidade da NF está em caixa e corrija com &quot;Corrigir qtd&quot; antes de aplicar.
                </p>
              </div>

              {suspiciousQtyItems > 0 && (
                <div className="incoming-pricing-summary-warning">
                  <AlertCircle size={16} className="incoming-pricing-summary-warning-icon" />
                  <p className="incoming-pricing-summary-warning-text">
                    {suspiciousQtyItems} {suspiciousQtyItems === 1 ? 'item pode ter' : 'itens podem ter'} quantidade em caixa sem conversão para unidade.
                  </p>
                </div>
              )}

              {lowMarginItems > 0 && (
                <div className="incoming-pricing-summary-warning">
                  <AlertCircle size={16} className="incoming-pricing-summary-warning-icon" />
                  <p className="incoming-pricing-summary-warning-text">
                    Atenção: {lowMarginItems} {lowMarginItems === 1 ? 'item está' : 'itens estão'} com margem abaixo do mínimo operacional (30%).
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showConvertModal && convertingItem && (
        <div
          className="incoming-pricing-modal-overlay"
          onClick={() => !isConverting && setShowConvertModal(false)}
        >
          <div className="incoming-pricing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incoming-pricing-modal-header">
              <h3>Corrigir quantidade</h3>
              <button
                type="button"
                className="incoming-pricing-modal-close"
                onClick={() => !isConverting && setShowConvertModal(false)}
                disabled={isConverting}
              >
                <X size={20} />
              </button>
            </div>

            <div className="incoming-pricing-modal-content">
              <div className="incoming-pricing-modal-item-info">
                <p className="incoming-pricing-modal-item-label">Item</p>
                <p className="incoming-pricing-modal-item-name">{convertingItem.product_name}</p>
                <p className="incoming-pricing-modal-item-detail">
                  Quantidade atual: {convertingItem.quantity} {convertingItem.xml_unit || 'UN'}
                  {' · '}Custo un.: R$ {convertingItem.cost_price.toFixed(4)}
                </p>
                {stockLaunched && (
                  <p className="incoming-pricing-modal-stock-note">
                    O estoque desta NF já foi lançado. A conversão ajustará o saldo do produto automaticamente.
                  </p>
                )}
              </div>

              <div className="incoming-pricing-modal-section">
                <h4>Tipo de conversão</h4>
                <label className="incoming-pricing-modal-radio">
                  <input
                    type="radio"
                    name="pricingConversionType"
                    value="multiply"
                    checked={conversionType === 'multiply'}
                    onChange={() => setConversionType('multiply')}
                    disabled={isConverting}
                  />
                  Multiplicar (ex.: 1 CX × 100 = 100 UN)
                </label>
                <label className="incoming-pricing-modal-radio">
                  <input
                    type="radio"
                    name="pricingConversionType"
                    value="divide"
                    checked={conversionType === 'divide'}
                    onChange={() => setConversionType('divide')}
                    disabled={isConverting}
                  />
                  Dividir (ex.: 100 UN ÷ 100 = 1 CX)
                </label>
              </div>

              <div className="incoming-pricing-modal-section">
                <h4>{conversionType === 'multiply' ? 'Multiplicador' : 'Divisor'}</h4>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={conversionValue}
                  onChange={(e) => setConversionValue(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="Ex: 100"
                  disabled={isConverting}
                  autoFocus
                  className="incoming-pricing-modal-input"
                />
                {previewConvertedQty !== null && previewUnitCost !== null && (
                  <div className="incoming-pricing-modal-preview">
                    <p>
                      Resultado: <strong>{convertingItem.quantity}</strong>
                      {' '}{conversionType === 'multiply' ? '×' : '÷'}{' '}
                      <strong>{parseFloat(conversionValue).toFixed(2)}</strong>
                      {' = '}
                      <strong className="highlight">{previewConvertedQty}</strong>
                    </p>
                    <p>
                      Novo custo unitário: <strong>R$ {previewUnitCost.toFixed(4)}</strong>
                      {' '}(total da linha mantido)
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="incoming-pricing-modal-actions">
              <button
                type="button"
                className="incoming-pricing-modal-btn secondary"
                onClick={() => setShowConvertModal(false)}
                disabled={isConverting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="incoming-pricing-modal-btn primary"
                onClick={handleConvertQuantity}
                disabled={
                  isConverting ||
                  !conversionValue ||
                  isNaN(parseFloat(conversionValue)) ||
                  parseFloat(conversionValue) <= 0
                }
              >
                {isConverting ? (
                  <Loader2 size={16} className="incoming-pricing-spinner" />
                ) : (
                  <Calculator size={16} />
                )}
                Confirmar correção
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomingPricingPage;
